import React, { Fragment, forwardRef } from "react";
import { PAYMENT_META, RECIPIENT_META, STATUS_KEYS, STATUS_META } from "../../utils/constants";
import { formatDate, formatMoney, formatTime12Hr } from "../../utils/format";
import logoPdf from "../../assets/logo_pdf.svg";

const cb = (checked) => (checked ? "☑" : "☐");

const deliveryText = (job) => {
  const billing = (job.billing_address || "").trim();
  const delivery = (job.delivery_address || "").trim();
  if (!delivery || delivery === billing) return delivery === billing && billing ? "Same as billing address" : "-";
  return delivery;
};

const descriptionText = (job) => {
  const clean = (job.job_description || "").trim().replace(/\n\s*\n\s*\n+/g, "\n\n");
  return clean || "Job Description: As per item specifications below.";
};

const Signature = ({ src, alt }) => (
  <div className="doc-sig-img-wrap">
    {src ? <img src={src} alt={alt} /> : <span className="no-sig-text">[ Not Signed ]</span>}
  </div>
);

/* The printable single-page A4 job order (reference #printable-job-document), in the store's branding. */
const JobOrderDocument = forwardRef(function JobOrderDocument({ job, store, config }, ref) {
  const services = job.services || [];
  const items = job.items || [];
  const currency = job.currency || store.currency;

  return (
    <div id="printable-job-document" className="job-document" ref={ref}>
      <div className="doc-header">
        <div className="doc-logo-col">
          <img src={logoPdf} alt="DOTIZO ADVERTISING LLC" className="doc-logo-img" />
        </div>
        <div className="doc-title-col">
          <h1 className="doc-main-title">JOB ORDER</h1>
        </div>
      </div>

      <div className="doc-meta-row">
        <div className="doc-meta-cell">
          <span className="meta-lbl">Job Order No.</span>
          <span className="meta-val">{job.job_no || "-"}</span>
        </div>
        <div className="doc-meta-cell text-right">
          <span className="meta-lbl">Date</span>
          <span className="meta-val">{formatDate(job.order_date)}</span>
        </div>
      </div>

      <div className="doc-section">
        <div className="doc-section-header">CUSTOMER DETAILS</div>
        <div className="doc-row">
          <span className="doc-field-label">Customer / company name:</span>
          <span className="doc-field-value">{job.customer_name || "-"}</span>
        </div>
        <div className="doc-grid-2">
          <div className="doc-row">
            <span className="doc-field-label">Contact person</span>
            <span className="doc-field-value">{job.contact_person || "-"}</span>
          </div>
          <div className="doc-row">
            <span className="doc-field-label">Mobile</span>
            <span className="doc-field-value">{job.mobile || "-"}</span>
          </div>
        </div>
        <div className="doc-row">
          <span className="doc-field-label">Email</span>
          <span className="doc-field-value">{(job.email || "").trim() || "-"}</span>
        </div>
        <div className="doc-row">
          <span className="doc-field-label">Billing address</span>
          <span className="doc-field-value">{(job.billing_address || "").trim() || "-"}</span>
        </div>
        <div className="doc-row">
          <span className="doc-field-label">Delivery address</span>
          <span className="doc-field-value">{deliveryText(job)}</span>
        </div>
      </div>

      <div className="doc-section">
        <div className="doc-section-header">SERVICE / JOB TYPE</div>
        <div className="doc-checkboxes-grid">
          {config.services.map((service) => (
            <div className="doc-cb-item" key={service}>
              <span className="doc-cb">{cb(services.includes(service))}</span> {service}
              {service === "Other" && (
                <>
                  :{" "}
                  {services.includes("Other") && job.service_other && <span>{job.service_other}</span>}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="doc-section">
        <div className="doc-section-header">JOB DETAILS AND SPECIFICATIONS</div>
        <div className="doc-description-block">{descriptionText(job)}</div>

        <table className="doc-items-table">
          <thead>
            <tr>
              <th>Quantity</th>
              <th>Size / dimensions</th>
              <th>Material</th>
              <th>Color / finish</th>
              <th>Artwork / file details</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center" style={{ color: "#64748b", fontStyle: "italic", padding: "4px 6px" }}>
                  No itemized specifications recorded
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <tr key={index}>
                  <td>{item.quantity || "-"}</td>
                  <td>{item.dimensions || "-"}</td>
                  <td>{item.material || "-"}</td>
                  <td>{item.color || "-"}</td>
                  <td>{item.artwork || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="doc-grid-2 mt-1">
          <div className="doc-row">
            <span className="doc-field-label">Deadline / delivery date</span>
            <span className="doc-field-value">{formatDate(job.deadline_date)}</span>
          </div>
          <div className="doc-row">
            <span className="doc-field-label">Time</span>
            <span className="doc-field-value">{formatTime12Hr(job.deadline_time) || "-"}</span>
          </div>
        </div>
      </div>

      <div className="doc-section">
        <div className="doc-section-header">PRICE AND PAYMENT</div>
        <div className="doc-grid-3">
          <div className="doc-row">
            <span className="doc-field-label">Price / amount</span>
            <span className="doc-field-value bold-text">{formatMoney(job.total_price, currency)}</span>
          </div>
          <div className="doc-row">
            <span className="doc-field-label">Advance paid</span>
            <span className="doc-field-value">{formatMoney(job.advance_paid, currency)}</span>
          </div>
          <div className="doc-row">
            <span className="doc-field-label">Balance</span>
            <span className="doc-field-value bold-text red-text">{formatMoney(job.balance, currency)}</span>
          </div>
        </div>

        <div className="doc-payment-methods-row">
          <span className="doc-field-label">
            Currency: <strong>{currency}</strong>
          </span>
          <span className="doc-field-label">Payment:</span>
          {config.payment_methods.map((method) => (
            <Fragment key={method}>
              <span className="doc-cb">{cb(job.payment_method === method)}</span> {PAYMENT_META[method]?.doc || method}
              {method === "other" && (
                <>
                  :{" "}
                  {job.payment_method === "other" && job.payment_other && <span>{job.payment_other}</span>}
                </>
              )}
            </Fragment>
          ))}
        </div>

        <div className="doc-row">
          <span className="doc-field-label">Special instructions</span>
          <span className="doc-field-value">{(job.special_instructions || "").trim() || "-"}</span>
        </div>
      </div>

      <div className="doc-section">
        <div className="doc-section-header">APPROVAL AND PRODUCTION</div>

        <div className="doc-approval-grid">
          <div className="doc-sig-cell">
            <div className="doc-sig-label">Customer approval / signature</div>
            <Signature src={job.customer_signature} alt="Customer Approval Signature" />
          </div>
          <div className="doc-sig-cell text-right">
            <div className="doc-sig-label">Date</div>
            <div className="doc-sig-val">{formatDate(job.approval_date)}</div>
          </div>
        </div>

        <div className="doc-grid-2">
          <div className="doc-row">
            <span className="doc-field-label">Prepared by</span>
            <span className="doc-field-value">{job.prepared_by || "-"}</span>
          </div>
          <div className="doc-row">
            <span className="doc-field-label">Production / assigned to</span>
            <span className="doc-field-value">{job.assigned_to || "-"}</span>
          </div>
        </div>

        <div className="doc-status-acknowledgement-box">
          <div className="doc-status-line">
            <strong>Status:</strong>
            {STATUS_KEYS.map((key) => (
              <Fragment key={key}>
                {" "}
                <span className="doc-cb">{cb(job.status === key)}</span> {STATUS_META[key].label}
              </Fragment>
            ))}
          </div>

          <div className="doc-delivery-ack-line">
            <span>
              Delivery / collection acknowledgement: received by <strong>{job.recipient_name || "_________________"}</strong>
            </span>
            <span>
              Date <strong>{job.recipient_date ? formatDate(job.recipient_date) : "___________"}</strong>
            </span>
          </div>

          <div className="doc-approval-grid mt-1">
            <div className="doc-sig-cell">
              <div className="doc-sig-label">Recipient signature</div>
              <Signature src={job.recipient_signature} alt="Recipient Signature" />
            </div>
            <div className="doc-sig-cell text-right">
              <div className="doc-sig-types">
                {config.recipient_types.map((type) => (
                  <Fragment key={type}>
                    {" "}
                    <span className="doc-cb">{cb((job.recipient_type || "delivered") === type)}</span> {RECIPIENT_META[type]?.doc || type}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="doc-terms-footer">
          <strong>Terms:</strong> {config.terms}
        </div>
      </div>
    </div>
  );
});

export default JobOrderDocument;
